import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, X, ChevronDown, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LandingPage = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);

  const handleCTAClick = () => {
    navigate('/aplicar');
  };

  const openImageModal = (imageUrl, description) => {
    setSelectedImage({ url: imageUrl, description });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  // Galería de fotos actualizada con jugadores destacados
  const galleryImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/yy7b169y_IMG_5112.jpeg',
      description: 'Atlético de Madrid - Fichaje juvenil en uno de los clubes más grandes de España',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/r25z95on_IMG_3679.heic',
      description: 'Fabián Hormazábal #17 - Selección Chilena y Universidad de Chile',
      objectFit: 'contain'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/l7gyp3ao_IMG_9163.jpeg',
      description: 'PSG - Paris Saint-Germain con Christiane Endler',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/jepto4zu_IMG_4482.heic',
      description: 'Fortaleza EC - Fichaje internacional en Brasil (Primera División)',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/7bia6y3u_IMG_8404.heic',
      description: 'Huachipato FC - Jugador profesional en Primera División chilena',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/q99n3d9r_9ff74805-3112-49e2-8974-5c436b723ed3.jpeg',
      description: 'Fichaje internacional - Jugadora profesional de élite',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/sip38fxb_IMG_9160.jpeg',
      description: 'Gestión de carrera - Artículo en medios especializados',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/9s7aqklf_IMG_9151.jpeg',
      description: 'La Roja - Trabajo con Selección Chilena de Fútbol Femenino',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/2p4fswrd_IMG_7105.jpeg',
      description: 'Solo Cracks - Reconocimiento como pionero del fútbol femenino',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/8t006w12_IMG_6434.png',
      description: 'Red internacional - Operaciones en 23 países',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/od3sisew_IMG_7327.jpeg',
      description: 'TNT Sports - Experto analista en medios deportivos',
      objectFit: 'cover'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/ov0648j7_6de69be1-837b-4ebb-8e91-dd50ec372e70.jpeg',
      description: 'El Jorge Mendes del fútbol femenino - Cobertura en prensa',
      objectFit: 'cover'
    }
  ];

  return (
    <div className="landing-page">
      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <button
            onClick={closeImageModal}
            className="absolute top-4 right-4 text-white hover:text-amber-400 transition-colors"
          >
            <XCircle className="w-10 h-10" />
          </button>
          <div className="max-w-6xl w-full">
            <img 
              src={selectedImage.url}
              alt={selectedImage.description}
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-4 text-lg">{selectedImage.description}</p>
          </div>
        </div>
      )}

      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container-custom py-4 flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/yf77589j_IMG_1273.png" 
            alt="Agente 360" 
            className="h-12 object-contain"
          />
          <Button 
            onClick={handleCTAClick}
            className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold hover:from-amber-500 hover:to-yellow-600 transition-all duration-300"
          >
            Aplicar Ahora
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-20 relative overflow-hidden bg-gradient-to-br from-black via-neutral-900 to-black">
        <div className="hero-bg-gradient"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="hero-content">
              <h1 className="hero-title mb-6">
                Quieres entrar en el mundo del fútbol…
                <br />
                <span className="text-amber-400">pero nadie te está enseñando</span>
                <br />
                cómo hacerlo de verdad.
              </h1>
              <div className="space-y-4 mb-8">
                <p className="text-xl md:text-2xl font-semibold text-white">
                  Agente 360 no es una formación.
                </p>
                <p className="text-xl md:text-2xl text-gray-300">
                  Es el lugar donde dejas de improvisar.
                </p>
                <p className="text-xl md:text-2xl text-amber-400 font-semibold">
                  Y empiezas a moverte como un agente real.
                </p>
              </div>
              <Button 
                onClick={handleCTAClick}
                size="lg"
                className="cta-button group"
              >
                Aplicar para una llamada
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="hero-image-container relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-400/20 to-transparent rounded-2xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/od3sisew_IMG_7327.jpeg"
                alt="Edgar Merino en TNT Sports"
                className="rounded-2xl shadow-2xl border border-amber-400/20 relative z-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Golpe/Reality Check Section */}
      <section className="py-20 bg-gradient-to-b from-neutral-900 to-black">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="section-title mb-8">Te lo digo claro:</h2>
            <p className="text-2xl md:text-3xl font-bold text-white mb-6">
              El problema no es que no puedas.
              <br />
              Es que estás jugando…{' '}
              <span className="text-amber-400">sin saber las reglas.</span>
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {[
                'La información no se comparte',
                'Los contactos no se regalan',
                'Y los errores se pagan caros'
              ].map((item, index) => (
                <Card key={index} className="card-hover p-6 bg-neutral-800/50 border-neutral-700">
                  <X className="h-8 w-8 text-red-500 mb-3 mx-auto" />
                  <p className="text-white font-medium">{item}</p>
                </Card>
              ))}
            </div>
            <p className="text-xl text-gray-400 mt-8 italic">
              Tiempo. Oportunidades. Credibilidad.
            </p>
          </div>
        </div>
      </section>

      {/* Problema Section */}
      <section className="py-20 bg-black">
        <div className="container-custom">
          <h2 className="section-title mb-12 text-center">
            Ahora mismo probablemente estás así:
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              'Quieres ser agente, pero no sabes por dónde empezar',
              'Hablas con jugadores… pero no avanzas',
              'Ves movimientos… pero no entiendes qué pasa detrás',
              'O ya estás dentro… pero estancado'
            ].map((problem, index) => (
              <Card key={index} className="card-hover p-6 bg-neutral-900/50 border-amber-400/20">
                <p className="text-gray-300">{problem}</p>
              </Card>
            ))}
          </div>
          <p className="text-center text-xl text-amber-400 font-semibold mt-12">
            Y lo peor: nadie te dice qué estás haciendo mal
          </p>
        </div>
      </section>

      {/* Quiebre Section */}
      <section className="py-20 bg-gradient-to-b from-black to-neutral-900">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="section-title mb-6">Yo también estuve ahí.</h2>
            <p className="text-2xl text-white mb-6">Y aprendí algo clave:</p>
            <div className="bg-amber-400/10 border-2 border-amber-400 rounded-2xl p-8 mb-6">
              <p className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">
                No necesitas más ganas
              </p>
              <p className="text-3xl md:text-4xl font-bold text-white">
                Necesitas criterio
              </p>
            </div>
            <p className="text-xl text-gray-400 italic">
              Porque sin criterio… Puedes estar años en el fútbol… y no avanzar nada.
            </p>
          </div>
        </div>
      </section>

      {/* Qué es Agente 360 */}
      <section className="py-20 bg-neutral-900">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="section-title mb-8 text-center">Qué es Agente 360</h2>
            <p className="text-2xl text-center text-white mb-6">
              Agente 360 es una mentoría práctica.
            </p>
            <p className="text-3xl text-center text-amber-400 font-bold mb-12">
              No teórica.
            </p>
            <div className="bg-black/50 border border-white/10 rounded-2xl p-8 md:p-12">
              <p className="text-xl md:text-2xl text-gray-300 mb-8 text-center">
                Aquí no vienes a escuchar historias.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  'Cómo se hacen fichajes reales',
                  'Cómo se construyen relaciones reales',
                  'Cómo se mueve el negocio de verdad',
                  'Cómo operas dentro del fútbol'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                    <p className="text-lg text-white">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diferencial Section */}
      <section className="py-20 bg-gradient-to-b from-neutral-900 to-black">
        <div className="container-custom">
          <h2 className="section-title mb-6 text-center">Hay miles de cursos.</h2>
          <p className="text-2xl text-center text-amber-400 font-bold mb-12">
            Pero ninguno tiene esto:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: '+700 fichajes',
                description: 'Experiencia real en más de 700 fichajes'
              },
              {
                title: 'Red internacional',
                description: 'Acceso a una red internacional de contactos'
              },
              {
                title: 'Estrategias reales',
                description: 'Estrategias usadas en operaciones reales'
              },
              {
                title: 'Oportunidades',
                description: 'Acceso a oportunidades reales'
              }
            ].map((item, index) => (
              <Card key={index} className="card-hover p-6 bg-amber-400/5 border-amber-400/30">
                <h3 className="text-2xl font-bold text-amber-400 mb-3">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </Card>
            ))}
          </div>
          <p className="text-center text-2xl text-white font-semibold mt-12">
            Aquí no aprendes sobre el fútbol
            <br />
            <span className="text-amber-400">Aprendes a operar dentro de él</span>
          </p>
        </div>
      </section>

      {/* Transformación Section */}
      <section className="py-20 bg-black">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="section-title mb-6 text-center">
              No te prometo dinero rápido.
            </h2>
            <p className="text-2xl text-center text-white mb-12">
              Pero sí algo mucho más potente:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Saber detectar talento de verdad',
                'Saber hablar con clubes',
                'Saber cuándo hay una oportunidad',
                'Saber cerrar una operación',
                'Saber posicionarte en la industria',
                'Saber moverte sin depender de nadie'
              ].map((skill, index) => (
                <div key={index} className="flex items-center gap-4 bg-neutral-900/50 border border-amber-400/20 rounded-xl p-4">
                  <div className="h-12 w-12 rounded-full bg-amber-400/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-6 w-6 text-amber-400" />
                  </div>
                  <p className="text-lg text-white font-medium">{skill}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Mí / Edgar Merino Section */}
      <section className="py-20 bg-gradient-to-b from-black to-neutral-900">
        <div className="container-custom">
          <h2 className="section-title mb-12 text-center">Sobre Mí</h2>
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 lg:order-1">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Soy Edgar Merino.
              </h3>
              <div className="space-y-4 text-lg text-gray-300">
                <p className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <span>He participado en más de 700 fichajes</span>
                </p>
                <p className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <span>He trabajado con jugadores, entrenadores y clubes en distintos mercados</span>
                </p>
                <p className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <span>He construido una red internacional dentro del fútbol</span>
                </p>
                <p className="flex items-start gap-3">
                  <Check className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <span>Y he visto cómo se gana… y cómo se pierde</span>
                </p>
              </div>
              <div className="mt-8 p-6 bg-amber-400/10 border-l-4 border-amber-400 rounded-r-xl">
                <p className="text-xl text-white font-semibold">
                  Agente 360 nace de eso.
                </p>
                <p className="text-lg text-gray-300 mt-2">
                  No de teoría. De experiencia.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/ov0648j7_6de69be1-837b-4ebb-8e91-dd50ec372e70.jpeg"
                alt="Edgar Merino"
                className="rounded-2xl shadow-2xl border border-white/10 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Credibilidad / Prueba Social */}
      <section className="py-20 bg-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title mb-4">Experiencia Real, Resultados Reales</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              He trabajado con jugadoras y jugadores en los clubes más importantes del mundo
            </p>
            <p className="text-sm text-gray-500 mt-2">Haz clic en las imágenes para verlas en grande</p>
          </div>

          {/* Grid de fotos - Ahora con 12 fotos clickeables (4x3) */}
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {galleryImages.map((image, index) => (
              <Card 
                key={index}
                className="card-hover p-0 overflow-hidden bg-neutral-900 border-white/10 cursor-pointer transform transition-all hover:scale-105 hover:border-amber-400/50"
                onClick={() => openImageModal(image.url, image.description)}
              >
                <div className="relative h-64 overflow-hidden bg-black">
                  <img 
                    src={image.url}
                    alt={image.description}
                    className={`w-full h-full transition-transform duration-300 hover:scale-110`}
                    style={{ objectFit: image.objectFit || 'cover' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <p className="text-white text-sm p-4">Haz clic para ver en grande</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-400 text-center line-clamp-2">{image.description}</p>
                </div>
              </Card>
            ))}
          </div>

          {/* Stats destacados */}
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto mt-16">
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-400 mb-2">+700</div>
              <p className="text-gray-400">Fichajes realizados</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-400 mb-2">23</div>
              <p className="text-gray-400">Países con operaciones</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-amber-400 mb-2">100%</div>
              <p className="text-gray-400">Experiencia real</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section className="py-20 bg-neutral-900">
        <div className="container-custom">
          <h2 className="section-title mb-12 text-center">Bonus</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: 'Comunidad de por vida',
                description: 'Acceso a comunidad de por vida'
              },
              {
                title: 'Bolsa de oportunidades',
                description: 'Bolsa de oportunidades reales con clubes'
              },
              {
                title: 'Mentoría personalizada',
                description: 'Mentoría personalizada'
              }
            ].map((bonus, index) => (
              <Card key={index} className="card-hover p-8 bg-black/50 border-amber-400/30 text-center">
                <div className="h-16 w-16 rounded-full bg-amber-400/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{bonus.title}</h3>
                <p className="text-gray-400">{bonus.description}</p>
              </Card>
            ))}
          </div>
          <p className="text-center text-2xl text-amber-400 font-semibold mt-12">
            No estás solo
          </p>
        </div>
      </section>

      {/* Cómo Funciona + Filtro Section */}
      <section className="py-20 bg-gradient-to-b from-neutral-900 to-black">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="section-title mb-12 text-center">Cómo Funciona</h2>
            <div className="grid md:grid-cols-4 gap-6 mb-16">
              {[
                { step: '1', text: 'Aplicas a la mentoría' },
                { step: '2', text: 'Tenemos una llamada contigo' },
                { step: '3', text: 'Vemos si encajas' },
                { step: '4', text: 'Y si tiene sentido, entras' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="h-16 w-16 rounded-full bg-amber-400 text-black text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                    {item.step}
                  </div>
                  <p className="text-white font-medium">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="bg-red-500/10 border-2 border-red-500 rounded-2xl p-8 text-center">
              <p className="text-2xl md:text-3xl font-bold text-red-500 mb-4">
                No todo el mundo entra
              </p>
              <div className="space-y-3 text-left max-w-2xl mx-auto mt-8">
                <p className="text-lg text-gray-300">Esto no es para todos.</p>
                <p className="flex items-start gap-3">
                  <X className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">No es para curiosos</span>
                </p>
                <p className="flex items-start gap-3">
                  <X className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">No es para el que busca dinero rápido</span>
                </p>
                <p className="flex items-start gap-3">
                  <X className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">No es para el que quiere probar</span>
                </p>
                <p className="flex items-start gap-3 pt-4">
                  <Check className="h-6 w-6 text-amber-400 flex-shrink-0 mt-1" />
                  <span className="text-white font-semibold text-xl">Es para quien quiere construir algo serio en el fútbol.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container-custom">
          <h2 className="section-title mb-12 text-center">Preguntas Frecuentes</h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {[
                {
                  question: '¿Necesito experiencia previa?',
                  answer: 'No. Pero sí compromiso.'
                },
                {
                  question: '¿Cuánto tiempo toma ver resultados?',
                  answer: 'Depende de ti. Aquí tienes dirección, no magia.'
                },
                {
                  question: '¿Es solo teoría?',
                  answer: 'No. Es práctica real aplicada al negocio.'
                },
                {
                  question: '¿Hay plazas limitadas?',
                  answer: 'Sí. Porque hay acompañamiento real.'
                }
              ].map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-neutral-900/50 border border-white/10 rounded-xl px-6 hover:border-amber-400/30 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-white hover:text-amber-400 py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-300 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-b from-black to-neutral-900">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Nadie te va a enseñar este negocio.
            </h2>
            <p className="text-xl text-gray-400 mb-4">No porque sea imposible.</p>
            <p className="text-xl text-gray-400 mb-8">Sino porque no interesa.</p>
            <p className="text-2xl text-white mb-12">
              Pero eso no significa que no puedas aprenderlo.
              <br />
              <span className="text-amber-400 font-semibold">
                Solo significa que tienes que elegir bien dónde hacerlo.
              </span>
            </p>
            
            <div className="bg-neutral-900 border border-amber-400/30 rounded-2xl p-8 md:p-12 mb-8">
              <p className="text-2xl text-white mb-8">
                Si quieres seguir probando solo… sigue.
              </p>
              <p className="text-3xl text-amber-400 font-bold mb-8">
                Si quieres hacerlo con criterio:
              </p>
              <Button 
                onClick={handleCTAClick}
                size="lg"
                className="cta-button-large group"
              >
                Aplica ahora a Agente 360
                <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-white/10">
        <div className="container-custom text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/yf77589j_IMG_1273.png" 
            alt="Agente 360" 
            className="h-16 object-contain mx-auto mb-6"
          />
          <p className="text-gray-400">
            © 2025 Agente 360. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
